import { Location, NgIf, NgStyle } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  TemplateRef,
  computed,
  effect,
  inject,
  signal,
  untracked,
  viewChild,
  viewChildren,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { FormArray, FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';

import { MatButton } from '@angular/material/button';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { RootState } from '@next/core-lib/app';
import { BrandingService } from '@next/core-lib/branding';
import {
  PageDetailComponent,
  PrintPageComponent,
  SummaryCardComponent,
} from '@next/core-lib/components';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { AgrNumberDirective } from '@next/core-lib/form';
import { AgrDatePipe, LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { RouterSelectors } from '@next/core-lib/router-store';

import { SettingsStorage } from '@next/core-lib/settings';
import { TableColumn, TableColumnTypes, TableConfig, TableComponent } from '@next/core-lib/table';
import { UserSelectors } from '@next/core-lib/user';
import { select, Store } from '@ngrx/store';
import { distinctUntilChanged, take } from 'rxjs/operators';
import { BrandingLogoSrc } from 'src/app/branding/branding.model';
import {
  getDayOffset,
  getRVSSettings,
  getUnitOfMeasureSettings,
} from 'src/app/settings/store/selectors/setting.selector';
import { PoultryContextKeys } from 'src/app/shared/constants';
import { SettingsServiceHelper } from 'src/app/shared/services/settingsHelperService';

import { ViewMode } from '../../../shared/models/shared.model';

import {
  ColumnSettings,
  ReferenceValueSheet,
  ReferenceValueSheetRow,
} from '../../models/reference-value-sheet.model';
import * as refValSheetActions from '../../store/reference-value-sheet.actions';

import {
  getBaseReferenceValueSheet,
  getColumnSettings,
  getSelectedCountry,
} from '../../store/reference-value-sheet.selectors';
import { ColumnSettingsComponent } from '../column-settings/column-settings.component';

@Component({
  selector: 'app-create-reference-value-sheet',
  templateUrl: './create-reference-value-sheet.component.html',
  styleUrls: ['./create-reference-value-sheet.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    PageDetailComponent,
    MatButton,
    MatIcon,
    NgIf,
    PrintPageComponent,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    TableComponent,
    NgStyle,
    AgrNumberDirective,
    SummaryCardComponent,
    TranslatePipe,
    AgrDatePipe,
  ],
})
export class CreateReferenceValueSheetComponent implements OnInit {
  private readonly store = inject<Store<RootState>>(Store);
  private readonly locale = inject<LocaleService>(LocaleService);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly location = inject(Location);
  private readonly datePipe = inject(AgrDatePipe);
  private readonly dialogService = inject(DialogService);
  private readonly settingsService = inject(SettingsServiceHelper);
  private readonly brandingService = inject(BrandingService);

  /* STORE SIGNALS */
  private readonly routeUrl = toSignal(this.store.pipe(select(RouterSelectors.getPath)), {
    initialValue: '',
  });
  readonly user = toSignal(this.store.select(UserSelectors.selectUser));
  readonly customer = toSignal(this.store.select(UserSelectors.selectCustomer));
  readonly locationData = toSignal(this.store.select(UserSelectors.selectLocation));
  private readonly columnSettingsSignal = toSignal(this.store.select(getColumnSettings), {
    initialValue: [] as string[],
  });
  private readonly rvsSettingsSignal = toSignal(this.store.select(getRVSSettings));
  private readonly countrySignal = toSignal(this.store.select(getSelectedCountry));
  private readonly dayOffsetSignal = toSignal(this.store.select(getDayOffset), { initialValue: 0 });
  private readonly baseRefValSheetSignal = toSignal(this.store.select(getBaseReferenceValueSheet));
  private readonly routeData = toSignal(this.activatedRoute.data);
  readonly unitOfMeasureSettings = toSignal(this.store.select(getUnitOfMeasureSettings));

  /* COMPUTED SIGNALS */
  readonly viewMode = computed(() => {
    const url = this.routeUrl();
    if (url.includes('reference-value-sheet/view/')) {
      return ViewMode.View;
    } else if (url.endsWith('reference-value-sheet/create')) {
      return ViewMode.Create;
    } else {
      return ViewMode.Edit;
    }
  });

  readonly showAgrifirmLogo = computed(() => !this.brandingService.isTenant('Agravis'));
  readonly customizedLogoSrc = computed(() =>
    this.brandingService.isTenant('Agravis')
      ? BrandingLogoSrc.AgravisLogo
      : BrandingLogoSrc.PoultryNextLogo,
  );

  readonly pageTitle = computed(() => {
    const viewMode = this.viewMode();
    const routeData = this.routeData();
    const refValSheet = this.refValSheet();
    if (!routeData) return '';
    const title = this.locale.translate(
      `${routeData['isStandard'] ? 'standard-' : ''}reference-value-sheet.create.title`,
    );
    switch (viewMode) {
      case ViewMode.View:
        return `${title}: ${refValSheet?.name ?? ''}`;
      case ViewMode.Create:
        return `${this.locale.translate('add')} ${title}`;
      case ViewMode.Edit:
        return `${this.locale.translate('edit')} ${title}: ${refValSheet?.name ?? ''}`;
    }
  });

  /* VIEW QUERIES (signal-based) */
  deleteRowTemplate = viewChild<TemplateRef<unknown>>('deleteRowTemplate');
  feedIntakeTotalTemplate = viewChild<TemplateRef<unknown>>('feedIntakeTotalTemplate');
  feedIntakeTotals = viewChildren('feedIntakeTotalInput', { read: MatInput });

  /* MUTABLE STATE SIGNALS */
  tableConfig = signal<TableConfig>({
    dataSource: new MatTableDataSource([] as ReferenceValueSheetRow[]),
    selectColumn: false,
    pagination: false,
    columns: [],
    enableArrowKeys: true,
  });

  refValSheet = signal<ReferenceValueSheet>(<ReferenceValueSheet>{});
  isNewRefValSheet = signal(false);
  isStandardRefValSheet = signal(false);
  tableColumns = signal<TableColumn[]>([]);
  displayedTableColumns = signal<TableColumn[]>([]);

  /* FORM CONTROLS */
  temperatureInsideControls = new FormArray([]);
  relativeHumidityControls = new FormArray([]);
  feedIntakeControls = new FormArray([]);
  waterIntakeControls = new FormArray([]);
  weightControls = new FormArray([]);
  mortalityControls = new FormArray([]);
  feedConversionControls = new FormArray([]);
  waterToFeedRatioControls = new FormArray([]);

  refValSheetGroup = new FormGroup({
    name: new FormControl(null, [Validators.required]),
    description: new FormControl(null),
    numberOfDays: new FormControl(null),
  });

  public viewModes: typeof ViewMode = ViewMode;

  /* EFFECTS */
  constructor() {
    /** Replaces ngAfterViewChecked - initializes feed intake totals when ViewChildren are available */
    effect(() => {
      const totals = this.feedIntakeTotals();
      const refValSheet = this.refValSheet();
      if (totals.length === refValSheet?.referenceValueSheetRows?.length && totals.length > 0) {
        refValSheet.referenceValueSheetRows.forEach(
          (row: ReferenceValueSheetRow, index: number) => {
            if (Number.isNaN(Number.parseFloat(totals[index]?.value))) {
              totals[index].value =
                row.feedIntakePerChickTotal === null ? '' : String(row.feedIntakePerChickTotal);
            }
          },
        );
      }
    });

    /** Applies templates to table columns when ViewChild templates become available */
    effect(() => {
      const deleteRowTpl = this.deleteRowTemplate();
      this.feedIntakeTotalTemplate(); // Track this signal
      const viewMode = this.viewMode();
      if (!deleteRowTpl || viewMode === ViewMode.View) return;
      untracked(() => {
        this.applyTemplates(viewMode);
      });
    });
  }

  ngOnInit(): void {
    const response = this.routeData();
    const viewMode = this.viewMode();
    const currentColumnSettings = this.columnSettingsSignal();
    const rvsSettings = this.rvsSettingsSignal();
    const baseRefValSheet = this.baseRefValSheetSignal();
    const dayOffset = this.dayOffsetSignal();

    if (viewMode === ViewMode.View) {
      this.tableConfig.set({
        dataSource: new MatTableDataSource([] as ReferenceValueSheetRow[]),
        selectColumn: false,
        pagination: false,
        columns: [
          {
            id: ColumnSettings.DAY,
            type: TableColumnTypes.STRING,
            label: this.locale.translate('reference-value-sheet.create.table-header.day'),
          },
          {
            id: ColumnSettings.TEMPERATURE_INSIDE,
            type: TableColumnTypes.NUMBER,
            digitsInfo: '1.1-1',
            label:
              this.locale.translate('reference-value-sheet.create.table-header.temperatureInside') +
              this.locale.translate(
                'rvs-setting.temperatureInside-option.short.' + rvsSettings.temperatureUnit,
              ),
          },
          {
            id: ColumnSettings.RELATIVE_HUMIDITY,
            type: TableColumnTypes.NUMBER,
            label: this.locale.translate(
              'reference-value-sheet.create.table-header.relativeHumidity',
            ),
          },
          {
            id: ColumnSettings.FEED_INTAKE_PER_CHICK_PER_DAY,
            type: TableColumnTypes.NUMBER,
            label:
              this.locale.translate('reference-value-sheet.create.table-header.feedIntake') +
              this.locale.translate('rvs-setting.feedIntake-option.short.' + rvsSettings.feedUnit),
          },
          {
            id: ColumnSettings.FEED_INTAKE_PER_CHICK_TOTAL,
            type: TableColumnTypes.NUMBER,
            label:
              this.locale.translate('reference-value-sheet.create.table-header.feedIntakeTotal') +
              this.locale.translate('rvs-setting.feedIntake-option.short.' + rvsSettings.feedUnit),
          },
          {
            id: ColumnSettings.WATER_INTAKE,
            type: TableColumnTypes.NUMBER,
            label:
              this.locale.translate('reference-value-sheet.create.table-header.waterIntake') +
              this.locale.translate(
                'rvs-setting.waterIntake-option.short.' + rvsSettings.waterUnit,
              ),
          },
          {
            id: ColumnSettings.WEIGHT,
            type: TableColumnTypes.NUMBER,
            label:
              this.locale.translate('reference-value-sheet.create.table-header.weight') +
              this.locale.translate('rvs-setting.weight-option.short.' + rvsSettings.weightUnit),
          },
          {
            id: ColumnSettings.MORTALITY,
            type: TableColumnTypes.NUMBER,
            label: this.locale.translate('reference-value-sheet.create.table-header.mortality'),
          },
          {
            id: ColumnSettings.FEED_CONVERSION,
            type: TableColumnTypes.NUMBER,
            digitsInfo: '1.3-3',
            label: this.locale.translate(
              'reference-value-sheet.create.table-header.feedConversion',
            ),
          },
          {
            id: ColumnSettings.WATER_TO_FEED_RATIO,
            type: TableColumnTypes.NUMBER,
            digitsInfo: '1.2-2',
            label: this.locale.translate(
              'reference-value-sheet.create.table-header.waterToFeedRatio',
            ),
          },
        ],
        scrolling: true,
        width: '1200px',
        wrapperWidth: '100%',
        dragDrop: false,
      });
    } else {
      this.tableConfig.set({
        dataSource: new MatTableDataSource([] as ReferenceValueSheetRow[]),
        selectColumn: false,
        pagination: false,
        columns: [
          {
            id: ColumnSettings.DAY,
            type: TableColumnTypes.STRING,
            label: this.locale.translate('reference-value-sheet.create.table-header.day'),
          },
          {
            id: ColumnSettings.TEMPERATURE_INSIDE,
            type: TableColumnTypes.INPUT_NUMBER,
            inputConfig: {
              step: 0.01,
              formArray: this.temperatureInsideControls,
            },
            label:
              this.locale.translate('reference-value-sheet.create.table-header.temperatureInside') +
              this.locale.translate(
                'rvs-setting.temperatureInside-option.short.' + rvsSettings.temperatureUnit,
              ),
          },
          {
            id: ColumnSettings.RELATIVE_HUMIDITY,
            type: TableColumnTypes.INPUT_NUMBER,
            inputConfig: {
              step: 0.01,
              formArray: this.relativeHumidityControls,
            },
            label: this.locale.translate(
              'reference-value-sheet.create.table-header.relativeHumidity',
            ),
          },
          {
            id: ColumnSettings.FEED_INTAKE_PER_CHICK_PER_DAY,
            type: TableColumnTypes.INPUT_NUMBER,
            inputConfig: {
              step: 0.01,
              formArray: this.feedIntakeControls,
            },
            label:
              this.locale.translate('reference-value-sheet.create.table-header.feedIntake') +
              this.locale.translate('rvs-setting.feedIntake-option.short.' + rvsSettings.feedUnit),
          },
          {
            id: ColumnSettings.FEED_INTAKE_PER_CHICK_TOTAL,
            type: TableColumnTypes.TEMPLATE,
            label:
              this.locale.translate('reference-value-sheet.create.table-header.feedIntakeTotal') +
              this.locale.translate('rvs-setting.feedIntake-option.short.' + rvsSettings.feedUnit),
          },
          {
            id: ColumnSettings.WATER_INTAKE,
            type: TableColumnTypes.INPUT_NUMBER,
            inputConfig: {
              step: 0.01,
              formArray: this.waterIntakeControls,
            },
            label:
              this.locale.translate('reference-value-sheet.create.table-header.waterIntake') +
              this.locale.translate(
                'rvs-setting.waterIntake-option.short.' + rvsSettings.waterUnit,
              ),
          },
          {
            id: ColumnSettings.WEIGHT,
            type: TableColumnTypes.INPUT_NUMBER,
            inputConfig: {
              step: 0.01,
              formArray: this.weightControls,
            },
            label:
              this.locale.translate('reference-value-sheet.create.table-header.weight') +
              this.locale.translate('rvs-setting.weight-option.short.' + rvsSettings.weightUnit),
          },
          {
            id: ColumnSettings.MORTALITY,
            type: TableColumnTypes.INPUT_NUMBER,
            inputConfig: {
              step: 0.01,
              formArray: this.mortalityControls,
            },
            label: this.locale.translate('reference-value-sheet.create.table-header.mortality'),
          },
          {
            id: ColumnSettings.FEED_CONVERSION,
            type: TableColumnTypes.INPUT_NUMBER,
            inputConfig: {
              step: 0.001,
              formArray: this.feedConversionControls,
            },
            label: this.locale.translate(
              'reference-value-sheet.create.table-header.feedConversion',
            ),
          },
          {
            id: ColumnSettings.WATER_TO_FEED_RATIO,
            type: TableColumnTypes.INPUT_NUMBER,
            inputConfig: {
              step: 0.01,
              formArray: this.waterToFeedRatioControls,
            },
            label: this.locale.translate(
              'reference-value-sheet.create.table-header.waterToFeedRatio',
            ),
          },
          {
            id: ColumnSettings.DELETE_ROW,
            type: TableColumnTypes.TEMPLATE,
            label: '',
          },
        ],
        scrolling: true,
        width: '1200px',
        wrapperWidth: '100%',
        enableArrowKeys: true,
      });
    }

    this.refValSheetGroup.controls.numberOfDays.setValue(0);

    const allTableColumns = this.tableConfig().columns;
    this.tableColumns.set(allTableColumns);
    this.displayedTableColumns.set(
      allTableColumns.filter((x) => currentColumnSettings.includes(x.id)),
    );

    this.tableConfig.update((config) => ({
      ...config,
      columns: this.displayedTableColumns(),
    }));

    this.feedIntakeControls.valueChanges
      .pipe(
        distinctUntilChanged(
          (a, b) =>
            JSON.stringify(a) === JSON.stringify(b) ||
            (a.length === 0 && b[0] === null) ||
            (b.length === 0 && a[0] === null),
        ),
      )
      .subscribe((values: number[]) => this.calculateCumulativeFeedintake(values));

    this.isStandardRefValSheet.set(response?.['isStandard'] ?? false);

    // eslint-disable-next-line
    if (response?.hasOwnProperty('refValSheet')) {
      // EDIT
      this.isNewRefValSheet.set(false);
      const editRefValSheet = { ...(<ReferenceValueSheet>response['refValSheet']) };
      this.refValSheet.set(editRefValSheet);
      this.refValSheetGroup.controls.name.setValue(editRefValSheet.name);
      this.refValSheetGroup.controls.description.setValue(editRefValSheet.description);
      const changedRows = editRefValSheet.referenceValueSheetRows.map((row) => ({
        ...row,
        day: this.calculateDay(row.day, dayOffset, true),
      }));
      this.tableConfig().dataSource.data = changedRows
        .map((row) => ({ ...row }))
        .sort((a, b) => (a.id > b.id ? 1 : -1));
    } else {
      // CREATE NEW
      this.isNewRefValSheet.set(true);
      if (baseRefValSheet) {
        this.refValSheet.set(<ReferenceValueSheet>{});

        this.refValSheetGroup.controls.name.setValue(baseRefValSheet?.name);
        this.refValSheetGroup.controls.description.setValue(baseRefValSheet?.description);
        this.tableConfig().dataSource.data = baseRefValSheet.referenceValueSheetRows.map((row) => ({
          ...row,
          day: this.calculateDay(row.day, dayOffset, true),
        }));
      } else {
        this.settingsService
          .getSingle(null, PoultryContextKeys.REFERENCE_VALUE_SHEET, SettingsStorage.AppLocal)
          .subscribe((base: ReferenceValueSheet) => {
            if (base) {
              this.refValSheet.set({
                ...base,
                isStandardReferenceValueSheet: false,
                standardReferenceValueSheetId: base.id,
                country: null,
                id: 0,
              });

              this.refValSheetGroup.controls.name.setValue(base?.name);
              this.refValSheetGroup.controls.description.setValue(base?.description);

              this.tableConfig().dataSource.data = base.referenceValueSheetRows;
            } else {
              this.refValSheet.set(<ReferenceValueSheet>{});
              this.tableConfig().dataSource.data = [];
            }
          });
      }
    }
  }

  calculateCumulativeFeedintake(feedIntakes: number[]) {
    let cumulative = 0;
    const totals = this.feedIntakeTotals();
    if (!!feedIntakes && feedIntakes.length === this.tableConfig().dataSource.data.length) {
      feedIntakes.forEach((feedIntake, index) => {
        cumulative = Math.round((cumulative + (feedIntake ?? 0)) * 100) / 100;
        if (totals.length > 0 && totals[index]) {
          totals[index].value = <string>(<unknown>cumulative);
        }
      });
    }
  }

  hasDayValueRows(): boolean {
    return this.tableConfig().dataSource.data.length > 0;
  }

  isLastRow(refValSheetRow: ReferenceValueSheetRow): boolean {
    const lastDay = Math.max(
      ...this.tableConfig().dataSource.data.map((row: ReferenceValueSheetRow) => row.day),
    );
    return refValSheetRow.day === lastDay;
  }

  hasNoNumberOfDays(): boolean {
    const nrOfDays = <number>this.refValSheetGroup.controls.numberOfDays.value;
    return !nrOfDays || nrOfDays === 0;
  }

  deleteDayValuesRow(refValSheetRow: ReferenceValueSheetRow) {
    this.updateDataSource();
    const config = this.tableConfig();
    config.dataSource.data = [
      ...config.dataSource.data
        .filter((row: ReferenceValueSheetRow) => row.id !== refValSheetRow.id)
        .sort((a: ReferenceValueSheetRow, b: ReferenceValueSheetRow) => (a.id > b.id ? 1 : -1)),
    ];
    // recalculate the cumulative column in a timeout, because otherwise the input fields are not yet updated.
    setTimeout(() => {
      this.calculateCumulativeFeedintake(
        config.dataSource.data.map((row: ReferenceValueSheetRow) => row.feedIntakePerChickPerDay),
      );
    }, 50);
  }

  addOneDayValuesRow() {
    this.updateDataSource();
    const config = this.tableConfig();
    config.dataSource.data = [
      ...config.dataSource.data,
      {
        id:
          config.dataSource.data.length > 0
            ? Math.max(...config.dataSource.data.map((row: ReferenceValueSheetRow) => row.id)) + 1
            : 0,
        day:
          config.dataSource.data.length > 0
            ? Math.max(...config.dataSource.data.map((row: ReferenceValueSheetRow) => row.day)) + 1
            : 1,
        temperatureInside: null,
        relativeHumidity: null,
        feedIntakePerChickPerDay: null,
        feedIntakePerChickTotal: null,
        waterIntake: null,
        weight: null,
        mortality: null,
        feedConversion: null,
        waterToFeedRatio: null,
      },
    ];
    // recalculate the cumulative column in a timeout, because otherwise the input fields are not yet updated.
    setTimeout(() => {
      this.calculateCumulativeFeedintake(
        config.dataSource.data.map((row: ReferenceValueSheetRow) => row.feedIntakePerChickPerDay),
      );
    }, 50);
  }

  addMultipleDayValuesRows() {
    const dayOffset = this.dayOffsetSignal();
    const config = this.tableConfig();
    const nrOfDays = <number>this.refValSheetGroup.controls.numberOfDays.value;
    if (nrOfDays > 0 && config.dataSource.data.length === 0) {
      for (let day = 0; day < nrOfDays; day++) {
        config.dataSource.data = [
          ...config.dataSource.data,
          {
            id: day,
            day: this.calculateDay(day, dayOffset, true),
            temperatureInside: null,
            relativeHumidity: null,
            feedIntakePerChickPerDay: null,
            feedIntakePerChickTotal: null,
            waterIntake: null,
            weight: null,
            mortality: null,
            feedConversion: null,
            waterToFeedRatio: null,
          },
        ];
      }
    }
  }

  printRefValSheet() {
    const existingTitle = document.title;
    const proposedTitle = `PoultryNEXT ${this.locale.translate(
      'reference-value-sheet.create.title',
    )} - ${this.datePipe.transform(String(new Date()))}`;
    if (globalThis) {
      document.title = proposedTitle;
      globalThis.print();
      document.title = existingTitle;
    }
  }

  openColumnSettings() {
    this.dialogService
      .form(ColumnSettingsComponent, {})
      .pipe(take(1))
      .subscribe((result) => {
        const viewMode = this.viewMode();
        if (result.status === DIALOG_RETURN_TYPES.confirm) {
          this.displayedTableColumns.set(
            this.tableColumns().filter((x) => result.result?.displayedColumns.includes(x.id)),
          );
          if (viewMode !== ViewMode.View) {
            this.updateDataSource();
          }
          this.tableConfig.update((config) => ({
            ...config,
            columns: this.displayedTableColumns(),
          }));

          this.applyTemplates(viewMode);
        }
      });
  }

  private applyTemplates(viewMode: ViewMode) {
    if (viewMode !== ViewMode.View) {
      const displayedCols = [...this.displayedTableColumns()];
      const deleteRowCol = this.tableColumns().find((c) => c.id === ColumnSettings.DELETE_ROW);

      if (deleteRowCol && !displayedCols.some((c) => c.id === ColumnSettings.DELETE_ROW)) {
        displayedCols.push(deleteRowCol);
        this.displayedTableColumns.set(displayedCols);
      }

      const newConfig = { ...this.tableConfig(), columns: [...displayedCols] };
      const columnDeleteRow = newConfig.columns.find((c) => c.id === ColumnSettings.DELETE_ROW);
      const columnFeedIntakeTotal = newConfig.columns.find(
        (c) => c.id === ColumnSettings.FEED_INTAKE_PER_CHICK_TOTAL,
      );

      if (columnDeleteRow) {
        columnDeleteRow.template = this.deleteRowTemplate();
      }
      if (columnFeedIntakeTotal) {
        columnFeedIntakeTotal.template = this.feedIntakeTotalTemplate();
      }
      this.tableConfig.set(newConfig);
    }
  }

  back(saved = false) {
    if (saved || this.router.navigated) {
      this.location.back();
      return;
    }

    this.router.navigate(['../'], { relativeTo: this.activatedRoute });
  }

  private updateDataSource() {
    const config = this.tableConfig();
    const totals = this.feedIntakeTotals();
    const refValSheetRows = config.dataSource.data.map((row: ReferenceValueSheetRow) => ({
      ...row,
    }));

    refValSheetRows.forEach((row) => {
      row.temperatureInside = <number>this.temperatureInsideControls.value[row.id];
      row.relativeHumidity = <number>this.relativeHumidityControls.value[row.id];
      row.feedIntakePerChickPerDay = <number>this.feedIntakeControls.value[row.id];
      row.feedIntakePerChickTotal = Number.parseFloat(<string>(<unknown>totals[row.id]?.value));
      row.waterIntake = <number>this.waterIntakeControls.value[row.id];
      row.weight = <number>this.weightControls.value[row.id];
      row.mortality = <number>this.mortalityControls.value[row.id];
      row.feedConversion = <number>this.feedConversionControls.value[row.id];
      row.waterToFeedRatio = <number>this.waterToFeedRatioControls.value[row.id];
    });
    config.dataSource.data = refValSheetRows;
  }

  private validateRefValSheet(): boolean {
    this.refValSheetGroup.markAllAsTouched();
    if (this.refValSheetGroup.valid) {
      return true;
    }
    let description = '';
    if (!this.refValSheetGroup.valid) {
      description += `<li>${this.locale.translate(
        'reference-value-sheet.create.validation-error.refvalsheet-name',
      )}</li>`;
    }

    this.dialogService.notify({
      title: this.locale.translate('reference-value-sheet.create.validation-error.title'),
      text: `<ul>${description}</ul>`,
    });
    return false;
  }

  save() {
    if (this.validateRefValSheet()) {
      this.updateDataSource();
      const user = this.user();
      const rvsSettings = this.rvsSettingsSignal();
      const locationVal = this.locationData();
      const country = this.countrySignal();
      const dayOffset = this.dayOffsetSignal();
      const currentRefValSheet = this.refValSheet();
      const config = this.tableConfig();
      const currentDisplayedColumns = this.displayedTableColumns();

      if (!rvsSettings) return;

      const updatedRefValSheet: ReferenceValueSheet = {
        ...currentRefValSheet,
        country: this.isStandardRefValSheet() ? country : locationVal?.country,
        updatedAdvisorName: user.name,
        isStandardReferenceValueSheet: this.isStandardRefValSheet(),
        createdAdvisorName: this.isNewRefValSheet()
          ? user.name
          : currentRefValSheet.createdAdvisorName,
        name: <string>this.refValSheetGroup.controls.name.value,
        description: <string>this.refValSheetGroup.controls.description.value,
        referenceValueSheetRows: <ReferenceValueSheetRow[]>config.dataSource.data.map(
          (row: ReferenceValueSheetRow) => ({
            id: row.id,
            day: this.calculateDay(row.day, dayOffset, false),
            temperatureInside: currentDisplayedColumns.some(
              (x) => x.id === ColumnSettings.TEMPERATURE_INSIDE,
            )
              ? row.temperatureInside
              : null,
            relativeHumidity: currentDisplayedColumns.some(
              (x) => x.id === ColumnSettings.RELATIVE_HUMIDITY,
            )
              ? row.relativeHumidity
              : null,
            feedIntakePerChickPerDay: currentDisplayedColumns.some(
              (x) => x.id === ColumnSettings.FEED_INTAKE_PER_CHICK_PER_DAY,
            )
              ? row.feedIntakePerChickPerDay
              : null,
            feedIntakePerChickTotal: currentDisplayedColumns.some(
              (x) => x.id === ColumnSettings.FEED_INTAKE_PER_CHICK_TOTAL,
            )
              ? row.feedIntakePerChickTotal
              : null,
            waterIntake: currentDisplayedColumns.some((x) => x.id === ColumnSettings.WATER_INTAKE)
              ? row.waterIntake
              : null,
            weight: currentDisplayedColumns.some((x) => x.id === ColumnSettings.WEIGHT)
              ? row.weight
              : null,
            mortality: currentDisplayedColumns.some((x) => x.id === ColumnSettings.MORTALITY)
              ? row.mortality
              : null,
            feedConversion: currentDisplayedColumns.some(
              (x) => x.id === ColumnSettings.FEED_CONVERSION,
            )
              ? row.feedConversion
              : null,
            waterToFeedRatio: currentDisplayedColumns.some(
              (x) => x.id === ColumnSettings.WATER_TO_FEED_RATIO,
            )
              ? row.waterToFeedRatio
              : null,
          }),
        ),
        temperatureUnit: rvsSettings.temperatureUnit,
        feedUnit: rvsSettings.feedUnit,
        waterUnit: rvsSettings.waterUnit,
        weightUnit: rvsSettings.weightUnit,
      };

      this.refValSheet.set(updatedRefValSheet);
      this.store.dispatch(
        refValSheetActions.upsertRefValSheetRequest({
          refValSheet: updatedRefValSheet,
          upsertType: 'create',
        }),
      );
    }
  }

  calculateDay(day: number, dayOffset: number, positive: boolean): number {
    let dayOffsetSetting = dayOffset;
    if (dayOffset === undefined) {
      dayOffsetSetting = 0;
    }
    if (positive) {
      return Number(day) + Number(dayOffsetSetting);
    } else {
      return Number(day) - Number(dayOffsetSetting);
    }
  }
}
